import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Bot, ArrowDownToLine, ArrowUpFromLine, GitBranch, Receipt, ShieldCheck, Users2, Settings, LogOut, Megaphone, UserCog, LineChart, Trophy, Lock, Building2, Smartphone, LifeBuoy, FileText, Info, Landmark, Coins, Plane } from "lucide-react";
import { toast } from "sonner";
import { BrandLockup } from "@/components/BrandLogo";

const userLinks = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "nav-dashboard" },
  { to: "/bots", label: "Bots", icon: Bot, id: "nav-bots" },
  { to: "/staking", label: "Staking", icon: Lock, id: "nav-staking" },
  { to: "/nb-token", label: "NB Token", icon: Coins, id: "nav-nb-token" },
  { to: "/credit", label: "Credit Line", icon: Landmark, id: "nav-credit" },
  { to: "/shareholder", label: "Shareholder", icon: Building2, id: "nav-shareholder" },
  { to: "/trades", label: "Trades", icon: LineChart, id: "nav-trades" },
  { to: "/deposit", label: "Deposit", icon: ArrowDownToLine, id: "nav-deposit" },
  { to: "/withdraw", label: "Withdraw", icon: ArrowUpFromLine, id: "nav-withdraw" },
  { to: "/team", label: "Team Tree", icon: GitBranch, id: "nav-team" },
  { to: "/referrals", label: "Referrals", icon: Users2, id: "nav-referrals" },
  { to: "/leaderboard", label: "Leaderboard", icon: Trophy, id: "nav-leaderboard" },
  { to: "/transactions", label: "Transactions", icon: Receipt, id: "nav-transactions" },
  { to: "/kyc", label: "KYC", icon: ShieldCheck, id: "nav-kyc" },
  { to: "/profile", label: "Profile", icon: UserCog, id: "nav-profile" },
  { to: "/plan", label: "Plan / PDF", icon: FileText, id: "nav-plan" },
  { to: "/support", label: "Support", icon: LifeBuoy, id: "nav-support" },
  { to: "/about", label: "About Us", icon: Info, id: "nav-about" },
  { to: "/download-app", label: "Get Mobile App", icon: Smartphone, id: "nav-download-app" },
];

const adminLinks = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, id: "nav-admin-overview" },
  { to: "/admin/users", label: "Users", icon: Users2, id: "nav-admin-users" },
  { to: "/admin/deposits", label: "Deposits", icon: ArrowDownToLine, id: "nav-admin-deposits" },
  { to: "/admin/withdrawals", label: "Withdrawals", icon: ArrowUpFromLine, id: "nav-admin-withdrawals" },
  { to: "/admin/credit", label: "Credit Applications", icon: Landmark, id: "nav-admin-credit" },
  { to: "/admin/nb-token", label: "NB Token", icon: Coins, id: "nav-admin-nb-token" },
  { to: "/admin/kyc", label: "KYC Reviews", icon: ShieldCheck, id: "nav-admin-kyc" },
  { to: "/admin/bots", label: "Bot Control", icon: Bot, id: "nav-admin-bots" },
  { to: "/admin/staking", label: "Staking", icon: Lock, id: "nav-admin-staking" },
  { to: "/admin/shareholder", label: "Shareholder", icon: Building2, id: "nav-admin-shareholder" },
  { to: "/admin/support", label: "Support Tickets", icon: LifeBuoy, id: "nav-admin-support" },
  { to: "/admin/transactions", label: "Transactions", icon: Receipt, id: "nav-admin-txs" },
  { to: "/admin/config", label: "System Config", icon: Settings, id: "nav-admin-config" },
  { to: "/admin/tour-funds", label: "Tour Funds", icon: Plane, id: "nav-admin-tour-funds" },
  { to: "/admin/announcements", label: "Announcements", icon: Megaphone, id: "nav-admin-ann" },
];

export function Sidebar({ variant = "user" }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const links = variant === "admin" ? adminLinks : userLinks;

  const handleLogout = () => {
    logout();
    toast.success("Signed out");
    navigate("/");
  };

  return (
    <aside className="w-64 shrink-0 flex flex-col bg-[#0F1626] border-r border-white/10 max-h-[100dvh] h-full overflow-hidden">
      <div className="px-6 py-4 border-b border-white/10 shrink-0">
        <NavLink to="/" data-testid="sidebar-logo" className="flex items-center gap-2">
          <BrandLockup size={40} tagline={false}/>
        </NavLink>
        <div className="text-[10px] tracking-widest text-nb-cyan uppercase mt-2 pl-1">{variant === "admin" ? "Admin Console" : "Investor App"}</div>
      </div>
      <nav className="flex-1 min-h-0 px-3 py-3 space-y-1 overflow-y-auto">
        {links.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.to === "/admin"}
            data-testid={l.id}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                isActive ? "bg-[#8AE234]/10 text-[#8AE234] border border-[#8AE234]/20" : "text-slate-300 hover:text-white hover:bg-white/5"
              }`
            }
          >
            <l.icon size={18} />
            <span>{l.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="px-3 py-3 border-t border-white/10 shrink-0">
        <div className="px-3 py-2 mb-2">
          <div className="text-xs text-slate-500 uppercase tracking-wider">Signed in as</div>
          <div className="text-sm text-white font-medium truncate">{user?.name}</div>
          <div className="text-xs text-slate-400 truncate">{user?.email}</div>
        </div>
        {variant === "user" && user?.role !== "admin" && null}
        {user?.role === "admin" && variant === "user" && (
          <NavLink to="/admin" data-testid="switch-admin" className="flex items-center gap-2 text-xs px-3 py-2 text-cyan-300 hover:text-cyan-200">
            <Settings size={14} /> Switch to Admin
          </NavLink>
        )}
        {user?.role === "admin" && variant === "admin" && (
          <NavLink to="/dashboard" data-testid="switch-user" className="flex items-center gap-2 text-xs px-3 py-2 text-cyan-300 hover:text-cyan-200">
            <Settings size={14} /> Switch to User
          </NavLink>
        )}
        <button data-testid="btn-logout" onClick={handleLogout} className="w-full flex items-center gap-2 text-sm px-3 py-2 rounded-lg text-red-400 hover:bg-red-500/10">
          <LogOut size={16} /> Sign out
        </button>
      </div>
    </aside>
  );
}
